/** @jsx jsx */

/* eslint-disable camelcase */
import { jsx, useTheme } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import type { CatalogMastertracksV1Resource, DegreesV1Resource } from '__generated__/graphql-types';
import type Maybe from 'graphql/tsutils/Maybe';
import { branch, compose, setDisplayName, withProps } from 'recompose';

import Imgix from 'js/components/Imgix';
import connectToRouter from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import Ratings from 'bundles/browse/components/ProductCard/Ratings';
import { logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import { VIEWS_AND_ENROLLMENT_THRESHOLD } from 'bundles/enterprise-legacy-xdp/components/ProductMetrics';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
// Save/Unsave Experiment
import SavingBookmark from 'bundles/save/components/SavingBookmark';
import { LookupDegreeViaSlugQuery, LookupMastertrackViaSlugQuery } from 'bundles/save/queries/LookupQueries';
import type { SavedProductStringTypes } from 'bundles/save/types/SavedProductQueries';
import type { withSaveDataForSearchCards } from 'bundles/save/types/withSavedDataFromProducts';
import {
  SAVING_BOOKMARK_BLACK,
  getCleanIdFromObjectId,
  getSavingProductType,
  savedProductTypes,
} from 'bundles/save/utils/savingBookmarkUtils';
import ProductDifficultyLabel from 'bundles/search-common/components/ProductDifficultyLabel';
import { GUIDED_PROJECT, RHYME_PROJECT, SELF_PACED_PROJECT } from 'bundles/search-common/constants/entityTypes';
// CDS
import { isSearchTheme } from 'bundles/search-common/searchTheme';
import numberRenders from 'bundles/teach-course/lib/numberRenders';

import _t from 'i18n!nls/search-common';

type PropsFromCaller = withSaveDataForSearchCards & {
  avgProductRating?: Maybe<number>;
  numProductRatings?: Maybe<number>;
  productDifficultyLevel?: Maybe<string | false>;
  enrollments?: Maybe<number>;
  tagline?: Maybe<string>;
  isPartOfCourseraPlus?: boolean;
  entityType?: string;
  id?: string;
  isSaved?: boolean;
  loadingSavedProducts?: boolean;
  showCourseraLitePill?: boolean;
};

type PropsFromRouter = {
  isOnSearchPage: boolean;
};

type PropsFromLookup = {
  degreeID?: string;
  mastertrackID?: string;
};

type PropsFromResolvedID = {
  resolvedID: string;
};

type PropsToComponent = PropsFromCaller &
  PropsFromRouter &
  PropsFromLookup &
  PropsFromResolvedID &
  PropsFromCourseraPlusProductOwnerships;

const ProductInfo = ({
  avgProductRating,
  productDifficultyLevel,
  enrollments,
  tagline,
  numProductRatings,
  isPartOfCourseraPlus,
  ownsCourseraPlus,
  entityType,
  id,
  isSaved,
  loadingSavedProducts,
  showCourseraLitePill,
  resolvedID,
}: PropsToComponent): JSX.Element => {
  const theme = useTheme();
  if (!isSearchTheme(theme)) {
    throw new Error(
      'ProductInfo can only be used in the context of a SearchTheme. Wrap this component with a ThemeProvider.'
    );
  }
  const showEnrollments = enrollments && enrollments > VIEWS_AND_ENROLLMENT_THRESHOLD;
  const showCourseraPlus = ownsCourseraPlus || GrowthDiscoveryExperiments.get('showCourseraPlusPillOnSearch');

  const isEntityTypeExcludedFromCourseraLite =
    entityType && [GUIDED_PROJECT, RHYME_PROJECT, SELF_PACED_PROJECT].includes(entityType);
  const showCourseraLite = !showCourseraPlus && showCourseraLitePill && !isEntityTypeExcludedFromCourseraLite;

  return (
    <div className="rc-ProductInfo" css={theme.productInfo}>
      <div className="rating-enroll-wrapper">
        {!!avgProductRating && !!numProductRatings && numProductRatings > 5 && (
          <div className="info-item">
            <Ratings ratingCount={numProductRatings} averageFiveStarRating={Math.round(avgProductRating * 10) / 10} />
          </div>
        )}
        {!!showEnrollments && (
          <div className="enrollment info-item">
            <FormattedMessage
              message={_t('{numberOfStudents} students')}
              numberOfStudents={
                <span className="enrollment-number">{numberRenders.Formatters.largeNumber(enrollments)}</span>
              }
            />
          </div>
        )}
        {showCourseraPlus && isPartOfCourseraPlus && (
          <div className="info-item">
            <Imgix src={logo.PLUS_PILL_BLUE} alt={_t('Coursera Plus')} maxWidth={37} maxHeight={16} />
          </div>
        )}
        {showCourseraLite && isPartOfCourseraPlus && (
          <div className="info-item">
            <Imgix src={logo.LITE_PILL_BLUE} alt={_t('Coursera Plus')} maxWidth={37} maxHeight={16} />
          </div>
        )}
      </div>
      {tagline && <p className="tagline">{tagline}</p>}
      {productDifficultyLevel && <ProductDifficultyLabel productDifficultyLevel={productDifficultyLevel} />}
      {id && entityType && !loadingSavedProducts && (
        <SavingBookmark
          saved={isSaved}
          product={{
            product_id: resolvedID,
            product_type: getSavingProductType(entityType) as SavedProductStringTypes,
          }}
          color={SAVING_BOOKMARK_BLACK}
          shouldShowText={true}
          shouldUseSmallSize={true}
        />
      )}
    </div>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  setDisplayName('ProductInfo'),
  connectToRouter<PropsFromRouter, {}>((router) => {
    const isOnSearchPage = router.location.pathname.includes('/search');

    return {
      isOnSearchPage,
    };
  }),
  branch<PropsFromRouter>(
    // Testing to see if on search page. If it is, then we resolve the ID either by looking it up if
    // it's a degree, or by removing the type prefix if it is not.
    ({ isOnSearchPage }) => isOnSearchPage,
    branch<PropsFromCaller>(
      ({ entityType }) =>
        getSavingProductType(entityType) === savedProductTypes.DEGREE ||
        getSavingProductType(entityType) === savedProductTypes.MASTERTRACK,
      branch<PropsFromCaller>(
        ({ entityType }) => getSavingProductType(entityType) === savedProductTypes.DEGREE,
        compose(
          graphql(LookupDegreeViaSlugQuery, {
            options: ({ id }: { id: string }) => {
              return {
                variables: {
                  degreeSlug: getCleanIdFromObjectId(id),
                },
              };
            },
            props: ({ data }: { data?: { loading: boolean; DegreesV1Resource?: DegreesV1Resource } }) => {
              const { loading, DegreesV1Resource } = data || {};

              return {
                degreeID: !loading && DegreesV1Resource?.bySlug?.elements[0]?.id,
              };
            },
          }),
          withProps<PropsFromResolvedID, PropsFromLookup>(({ degreeID }) => ({ resolvedID: degreeID || '' }))
        ),
        compose(
          graphql(LookupMastertrackViaSlugQuery, {
            options: ({ id }: { id: string }) => {
              return {
                variables: {
                  mastertrackSlug: getCleanIdFromObjectId(id),
                  liveOnly: true,
                },
              };
            },
            props: ({
              data,
            }: {
              data?: { loading: boolean; CatalogMastertracksV1Resource?: CatalogMastertracksV1Resource };
            }) => {
              const { loading, CatalogMastertracksV1Resource } = data || {};

              return {
                mastertrackID: !loading && CatalogMastertracksV1Resource?.bySlug?.elements[0]?.id,
              };
            },
          }),
          withProps<PropsFromResolvedID, PropsFromLookup>(({ mastertrackID }) => ({ resolvedID: mastertrackID || '' }))
        )
      ),
      withProps<PropsFromResolvedID, PropsFromCaller>(({ id }) => ({
        resolvedID: getCleanIdFromObjectId(id || '') || '',
      }))
    ),
    // update resolvedID with ID passed in by default because if not on the search page,
    // those IDs are provided by the backend and have no need for augmentation
    withProps<PropsFromResolvedID, PropsFromCaller>(({ id }) => ({ resolvedID: id || '' }))
  ),
  withCourseraPlusProductOwnerships<PropsFromCaller>()
)(ProductInfo);
